import { useMemo, useState } from "react";
import {
  ArrowRight,
  CalendarDays,
  MapPin,
  Sparkles,
  Ticket,
  Users,
} from "lucide-react";
import { motion } from "framer-motion";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { scaleIn, stagger } from "@/lib/animations";
import Footer from "@/components/ui/Footer";
import Blob from "@/components/ui/Blob";
import RetreatLink from "@/components/retreats/RetreatLink";

/* ─── Shared retreat shape (subset of the Retreats route type) ─── */
export type RetreatCard = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  tagline: string | null;
  location: string;
  venue: string | null;
  startDate: string;
  endDate: string;
  capacity: number | null;
  status: "draft" | "open" | "sold-out" | "past";
  heroMediaId: string | null;
};

const mediaUrl = (mediaId: string) => `/media/${mediaId}`;

/* ─── Date helpers ─── */
function dateRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  const fmt = (d: Date, opts: Intl.DateTimeFormatOptions) =>
    d.toLocaleDateString(undefined, opts);
  if (sameMonth) {
    return `${fmt(s, { month: "long" })} ${s.getDate()}–${e.getDate()}, ${s.getFullYear()}`;
  }
  return `${fmt(s, { month: "short", day: "numeric" })} – ${fmt(e, { month: "short", day: "numeric", year: "numeric" })}`;
}

function shortDateParts(iso: string) {
  const d = new Date(iso);
  return {
    month: d.toLocaleDateString(undefined, { month: "short" }).toUpperCase(),
    day: d.getDate(),
    year: d.getFullYear(),
  };
}

function nights(start: string, end: string): number {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return Math.max(1, Math.round(ms / 86_400_000));
}

/* ─── Image / placeholder block, brand-tinted ─── */
function RetreatImage({
  retreat,
  className = "",
  rounded = "",
  tint,
}: {
  retreat: RetreatCard;
  className?: string;
  rounded?: string;
  tint?: string;
}) {
  const bg = tint ?? `${colors.sage}22`;
  if (retreat.heroMediaId) {
    return (
      <img
        src={mediaUrl(retreat.heroMediaId)}
        alt={retreat.title}
        loading="lazy"
        className={`object-cover ${rounded} ${className}`}
        style={{ backgroundColor: bg }}
      />
    );
  }
  return (
    <div
      className={`flex items-center justify-center ${rounded} ${className}`}
      style={{ backgroundColor: bg }}
    >
      <svg viewBox="0 0 120 120" className="h-1/2 w-1/2 opacity-30">
        <g fill="none" stroke={colors.forestGreen} strokeWidth={3} strokeLinecap="round">
          <path d="M20 90 Q40 55 60 70 T100 50" strokeDasharray="6 7" />
          <circle cx="60" cy="34" r="14" />
          <path d="M30 100h60" />
        </g>
      </svg>
    </div>
  );
}

function StatusBadge({
  status,
  tone = "light",
}: {
  status: RetreatCard["status"];
  tone?: "light" | "dark";
}) {
  if (status === "sold-out") {
    return (
      <span
        className="rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.16em]"
        style={{
          fontFamily: fonts.body,
          fontWeight: 700,
          backgroundColor: colors.berryRose,
          color: "white",
        }}
      >
        Sold out
      </span>
    );
  }
  if (status === "open") {
    return (
      <span
        className="rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.16em]"
        style={{
          fontFamily: fonts.body,
          fontWeight: 700,
          backgroundColor: tone === "dark" ? "rgba(255,255,255,0.16)" : `${colors.forestGreen}1f`,
          color: tone === "dark" ? "white" : colors.forestGreen,
        }}
      >
        Booking open
      </span>
    );
  }
  return (
    <span
      className="rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.16em]"
      style={{
        fontFamily: fonts.body,
        fontWeight: 700,
        backgroundColor: `${colors.navy}10`,
        color: `${colors.navy}88`,
      }}
    >
      Past retreat
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════
   DESIGN 1 — Postcard / Travel Stamp
   Hero image with a corner "destination stamp", dashed stitch frame,
   handwritten tagline. Reads like a keepsake postcard.
══════════════════════════════════════════════════════════════════ */
function PostcardCard({ retreat }: { retreat: RetreatCard }) {
  const sp = shortDateParts(retreat.startDate);
  return (
    <RetreatLink
      slug={retreat.slug}
      to={`/retreats/${retreat.slug}`}
      className="group block overflow-hidden rounded-[26px] transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
      style={{
        backgroundColor: "white",
        border: `2px dashed ${colors.tan}55`,
        boxShadow: "0 12px 28px rgba(8,45,61,0.07)",
      }}
    >
      <div className="relative h-52 overflow-hidden">
        <RetreatImage
          retreat={retreat}
          className="h-full w-full transition-transform duration-500 group-hover:scale-105"
        />
        {/* postmark stamp */}
        <div
          className="absolute right-3 top-3 flex h-16 w-16 flex-col items-center justify-center rounded-md"
          style={{
            backgroundColor: `${colors.cream}f2`,
            border: `2px dashed ${colors.berryRose}99`,
            transform: "rotate(6deg)",
          }}
        >
          <span
            className="text-[9px] uppercase tracking-[0.18em]"
            style={{ fontFamily: fonts.body, fontWeight: 700, color: colors.berryRose }}
          >
            {sp.month}
          </span>
          <span style={{ fontFamily: fonts.logo, fontSize: 26, lineHeight: 1, color: colors.navy }}>
            {sp.day}
          </span>
          <span
            className="text-[8px] tracking-[0.12em]"
            style={{ fontFamily: fonts.body, fontWeight: 600, color: `${colors.navy}99` }}
          >
            {sp.year}
          </span>
        </div>
      </div>

      <div className="p-5">
        {retreat.tagline && (
          <p
            className="mb-1"
            style={{ fontFamily: fonts.heading, fontSize: 20, color: colors.berryRose, lineHeight: 1 }}
          >
            {retreat.tagline}
          </p>
        )}
        <h3 className="mb-1.5 text-xl leading-snug" style={{ fontFamily: fonts.logo, color: colors.navy }}>
          {retreat.title}
        </h3>
        {retreat.subtitle && (
          <p className="mb-3 text-sm" style={{ fontFamily: fonts.body, color: `${colors.navy}99` }}>
            {retreat.subtitle}
          </p>
        )}
        <div
          className="flex items-center gap-1.5 border-t pt-3 text-[13px]"
          style={{ borderColor: `${colors.tan}33`, fontFamily: fonts.body, color: colors.navy }}
        >
          <MapPin size={14} style={{ color: colors.tan }} />
          <span style={{ fontWeight: 600 }}>{retreat.location}</span>
          <span style={{ color: `${colors.navy}55` }}>·</span>
          <span style={{ color: `${colors.navy}99` }}>{dateRange(retreat.startDate, retreat.endDate)}</span>
        </div>
      </div>
    </RetreatLink>
  );
}

/* ══════════════════════════════════════════════════════════════════
   DESIGN 2 — Boutique Event Ticket
   Perforated admission-pass layout: big date block stub on the left,
   notched tear line, "Reserve" tear-off CTA. Playful + retail.
══════════════════════════════════════════════════════════════════ */
function TicketCard({ retreat }: { retreat: RetreatCard }) {
  const sp = shortDateParts(retreat.startDate);
  const muted = retreat.status === "past";
  return (
    <RetreatLink
      slug={retreat.slug}
      to={`/retreats/${retreat.slug}`}
      className="group relative flex overflow-hidden rounded-[22px] transition-all duration-300 hover:-translate-y-1"
      style={{
        backgroundColor: "white",
        boxShadow: "0 12px 26px rgba(8,45,61,0.08)",
        opacity: muted ? 0.75 : 1,
      }}
    >
      {/* date stub */}
      <div
        className="flex w-24 flex-shrink-0 flex-col items-center justify-center gap-0.5 px-2 text-center"
        style={{ backgroundColor: colors.navy }}
      >
        <span
          className="text-[10px] uppercase tracking-[0.2em]"
          style={{ fontFamily: fonts.body, fontWeight: 700, color: colors.sage }}
        >
          {sp.month}
        </span>
        <span style={{ fontFamily: fonts.logo, fontSize: 40, lineHeight: 1, color: "white" }}>
          {sp.day}
        </span>
        <span
          className="text-[10px] tracking-[0.14em]"
          style={{ fontFamily: fonts.body, fontWeight: 600, color: `${colors.cream}aa` }}
        >
          {sp.year}
        </span>
        <span
          className="mt-2 rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.12em]"
          style={{ fontFamily: fonts.body, fontWeight: 700, backgroundColor: "rgba(255,255,255,0.14)", color: "white" }}
        >
          {nights(retreat.startDate, retreat.endDate)} nights
        </span>
      </div>

      {/* perforation */}
      <div className="relative w-0 flex-shrink-0">
        <div
          className="absolute inset-y-2 left-0 border-l-2 border-dashed"
          style={{ borderColor: `${colors.navy}22` }}
        />
        <span
          className="absolute -left-2 -top-2 h-4 w-4 rounded-full"
          style={{ backgroundColor: colors.cream }}
        />
        <span
          className="absolute -bottom-2 -left-2 h-4 w-4 rounded-full"
          style={{ backgroundColor: colors.cream }}
        />
      </div>

      {/* body */}
      <div className="flex flex-1 flex-col gap-2 p-5">
        <div className="flex items-start justify-between gap-2">
          <p
            className="text-[10px] uppercase tracking-[0.18em]"
            style={{ fontFamily: fonts.body, fontWeight: 700, color: colors.tan }}
          >
            <Ticket size={12} className="mr-1 inline" />
            Retreat Pass
          </p>
          <StatusBadge status={retreat.status} />
        </div>
        <h3 className="text-lg leading-snug" style={{ fontFamily: fonts.logo, color: colors.navy }}>
          {retreat.title}
        </h3>
        {retreat.subtitle && (
          <p className="text-sm" style={{ fontFamily: fonts.body, color: `${colors.navy}99` }}>
            {retreat.subtitle}
          </p>
        )}
        <div
          className="mt-auto flex items-center gap-1.5 text-[13px]"
          style={{ fontFamily: fonts.body, color: colors.navy }}
        >
          <MapPin size={14} style={{ color: colors.berryRose }} />
          <span style={{ fontWeight: 600 }}>{retreat.venue ?? retreat.location}</span>
        </div>
        <span
          className="mt-1 inline-flex w-fit items-center gap-1.5 text-sm transition-transform group-hover:translate-x-1"
          style={{ fontFamily: fonts.button, fontWeight: 700, color: colors.berryRose }}
        >
          Reserve your spot <ArrowRight size={15} />
        </span>
      </div>
    </RetreatLink>
  );
}

/* ══════════════════════════════════════════════════════════════════
   DESIGN 3 — Editorial Full-Bleed
   Image fills the card; gradient scrim; title + handwritten tagline
   set over the photo. Tall, magazine-cover energy.
══════════════════════════════════════════════════════════════════ */
function EditorialCard({ retreat }: { retreat: RetreatCard }) {
  return (
    <RetreatLink
      slug={retreat.slug}
      to={`/retreats/${retreat.slug}`}
      className="group relative block h-[420px] overflow-hidden rounded-[28px]"
      style={{ boxShadow: "0 16px 34px rgba(8,45,61,0.16)" }}
    >
      <RetreatImage
        retreat={retreat}
        tint={`${colors.forestGreen}33`}
        className="absolute inset-0 h-full w-full transition-transform duration-700 group-hover:scale-105"
      />
      {/* scrim */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(8,45,61,0.05) 0%, rgba(8,45,61,0.15) 45%, rgba(8,45,61,0.82) 100%)",
        }}
      />
      <div className="absolute left-5 right-5 top-5 flex items-center justify-between">
        <span
          className="rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.18em] backdrop-blur"
          style={{
            fontFamily: fonts.body,
            fontWeight: 700,
            backgroundColor: "rgba(255,255,255,0.18)",
            color: "white",
          }}
        >
          {dateRange(retreat.startDate, retreat.endDate)}
        </span>
        <StatusBadge status={retreat.status} tone="dark" />
      </div>

      <div className="absolute inset-x-0 bottom-0 p-6">
        {retreat.tagline && (
          <p className="mb-1" style={{ fontFamily: fonts.heading, fontSize: 24, color: colors.blushPink, lineHeight: 1 }}>
            {retreat.tagline}
          </p>
        )}
        <h3 className="text-3xl leading-tight" style={{ fontFamily: fonts.logo, color: "white" }}>
          {retreat.title}
        </h3>
        <div
          className="mt-2 flex items-center gap-1.5 text-sm"
          style={{ fontFamily: fonts.body, color: "rgba(255,255,255,0.85)" }}
        >
          <MapPin size={15} />
          <span style={{ fontWeight: 600 }}>{retreat.location}</span>
        </div>
        <span
          className="mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm transition-all group-hover:gap-3"
          style={{ fontFamily: fonts.button, fontWeight: 700, backgroundColor: "white", color: colors.navy }}
        >
          View retreat <ArrowRight size={16} />
        </span>
      </div>
    </RetreatLink>
  );
}

/* ══════════════════════════════════════════════════════════════════
   DESIGN 4 — Quilted Color-Block
   No-photo-needed: a patchwork color-block header with stitch dots,
   pill metadata chips below. Soft, crafty, on-brand for quilting.
══════════════════════════════════════════════════════════════════ */
const QUILT_TONES = [
  [colors.sage, colors.sageLight],
  [colors.denimBlue, colors.lavender],
  [colors.berryPink, colors.blushPink],
  [colors.tan, colors.warmCream],
];

function QuiltedCard({ retreat, index = 0 }: { retreat: RetreatCard; index?: number }) {
  const [a, b] = QUILT_TONES[index % QUILT_TONES.length];
  return (
    <RetreatLink
      slug={retreat.slug}
      to={`/retreats/${retreat.slug}`}
      className="group block overflow-hidden rounded-[26px] transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
      style={{ backgroundColor: "white", border: `1px solid ${colors.tan}22` }}
    >
      {/* patchwork header */}
      <div className="relative grid h-28 grid-cols-4 gap-1 p-1">
        {[a, b, b, a].map((c, i) => (
          <div
            key={i}
            className="rounded-[14px]"
            style={{ backgroundColor: c, opacity: 0.9 }}
          />
        ))}
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className="flex h-14 w-14 items-center justify-center rounded-full shadow-md"
            style={{ backgroundColor: colors.cream }}
          >
            <Sparkles size={22} style={{ color: colors.tan }} />
          </span>
        </div>
      </div>

      <div className="p-5">
        <h3 className="mb-1 text-xl leading-snug" style={{ fontFamily: fonts.logo, color: colors.navy }}>
          {retreat.title}
        </h3>
        {retreat.subtitle && (
          <p className="mb-3 text-sm" style={{ fontFamily: fonts.body, color: `${colors.navy}99` }}>
            {retreat.subtitle}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {[
            { icon: CalendarDays, text: dateRange(retreat.startDate, retreat.endDate) },
            { icon: MapPin, text: retreat.location },
            ...(retreat.capacity ? [{ icon: Users, text: `${retreat.capacity} spots` }] : []),
          ].map(({ icon: Icon, text }, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px]"
              style={{ fontFamily: fonts.body, fontWeight: 600, backgroundColor: `${colors.sage}1f`, color: colors.navy }}
            >
              <Icon size={13} style={{ color: colors.forestGreen }} />
              {text}
            </span>
          ))}
        </div>
      </div>
    </RetreatLink>
  );
}

/* ══════════════════════════════════════════════════════════════════
   DESIGN 5 — Catalog Spec Card
   Refined retail product card: image, structured spec rows, status
   chip, prominent navy CTA. Clean, scannable, "add to cart" feel.
══════════════════════════════════════════════════════════════════ */
function CatalogCard({ retreat }: { retreat: RetreatCard }) {
  const soldOut = retreat.status === "sold-out";
  const past = retreat.status === "past";
  const specs = [
    { label: "Dates", value: dateRange(retreat.startDate, retreat.endDate) },
    { label: "Location", value: retreat.venue ? `${retreat.venue}, ${retreat.location}` : retreat.location },
    ...(retreat.capacity ? [{ label: "Capacity", value: `${retreat.capacity} guests` }] : []),
    { label: "Length", value: `${nights(retreat.startDate, retreat.endDate)} nights` },
  ];
  return (
    <RetreatLink
      slug={retreat.slug}
      to={`/retreats/${retreat.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl transition-all duration-300 hover:shadow-xl"
      style={{ backgroundColor: "white", border: `1px solid ${colors.tan}25` }}
    >
      <div className="relative h-44 overflow-hidden" style={{ backgroundColor: `${colors.sage}14` }}>
        <RetreatImage
          retreat={retreat}
          className="h-full w-full transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute left-3 top-3">
          <StatusBadge status={retreat.status} />
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="text-lg leading-snug" style={{ fontFamily: fonts.logo, color: colors.navy }}>
          {retreat.title}
        </h3>
        {retreat.subtitle && (
          <p className="mt-0.5 text-sm" style={{ fontFamily: fonts.body, color: `${colors.navy}99` }}>
            {retreat.subtitle}
          </p>
        )}

        <dl className="my-4 flex flex-col gap-2">
          {specs.map((s) => (
            <div
              key={s.label}
              className="flex items-baseline justify-between gap-3 border-b pb-2 text-[13px] last:border-b-0 last:pb-0"
              style={{ borderColor: `${colors.navy}0f` }}
            >
              <dt
                className="uppercase tracking-[0.12em]"
                style={{ fontFamily: fonts.body, fontWeight: 700, color: `${colors.navy}66`, fontSize: 11 }}
              >
                {s.label}
              </dt>
              <dd className="text-right" style={{ fontFamily: fonts.body, fontWeight: 600, color: colors.navy }}>
                {s.value}
              </dd>
            </div>
          ))}
        </dl>

        <span
          className="mt-auto inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm transition-all"
          style={{
            fontFamily: fonts.button,
            fontWeight: 700,
            backgroundColor: past ? `${colors.navy}10` : soldOut ? colors.berryRose : colors.navy,
            color: past ? `${colors.navy}88` : "white",
          }}
        >
          {past ? "View recap" : soldOut ? "Join waitlist" : "Reserve your spot"}
          {!past && <ArrowRight size={16} />}
        </span>
      </div>
    </RetreatLink>
  );
}

/* ─── Direction registry ─── */
type Direction = {
  id: string;
  name: string;
  subtitle: string;
  format: string;
  cols: string;
  render: (retreat: RetreatCard, index: number) => React.ReactNode;
};

const DIRECTIONS: Direction[] = [
  {
    id: "postcard",
    name: "Travel Postcard",
    subtitle: "A keepsake-postcard frame with a postmark date stamp and handwritten tagline.",
    format: "Dashed-stitch frame · stamp",
    cols: "sm:grid-cols-2 lg:grid-cols-3",
    render: (r) => <PostcardCard retreat={r} />,
  },
  {
    id: "ticket",
    name: "Boutique Event Ticket",
    subtitle: "An admission-pass layout — date stub, perforated tear line, reserve tear-off.",
    format: "Perforated ticket stub",
    cols: "sm:grid-cols-2",
    render: (r) => <TicketCard retreat={r} />,
  },
  {
    id: "editorial",
    name: "Editorial Full-Bleed",
    subtitle: "Photo fills the tile with a gradient scrim and title set over the image.",
    format: "Full-bleed magazine cover",
    cols: "sm:grid-cols-2 lg:grid-cols-3",
    render: (r) => <EditorialCard retreat={r} />,
  },
  {
    id: "quilted",
    name: "Quilted Color-Block",
    subtitle: "Photo-free patchwork header with stitch motif and pill metadata chips.",
    format: "Color-block · no image needed",
    cols: "sm:grid-cols-2 lg:grid-cols-3",
    render: (r, i) => <QuiltedCard retreat={r} index={i} />,
  },
  {
    id: "catalog",
    name: "Catalog Spec Card",
    subtitle: "Refined retail card with structured spec rows and a prominent reserve CTA.",
    format: "Product catalog · spec table",
    cols: "sm:grid-cols-2 lg:grid-cols-3",
    render: (r) => <CatalogCard retreat={r} />,
  },
];

function DirectionHeader({ dir, index }: { dir: Direction; index: number }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p
          className="mb-1 text-[10px] uppercase tracking-[0.22em]"
          style={{ fontFamily: fonts.body, fontWeight: 700, color: `${colors.navy}77` }}
        >
          Direction {index + 1}
        </p>
        <h2 className="text-2xl sm:text-3xl" style={{ fontFamily: fonts.logo, color: colors.navy }}>
          {dir.name}
        </h2>
        <p
          className="mt-1 text-sm"
          style={{ fontFamily: fonts.body, color: `${colors.navy}99`, letterSpacing: "0.02em" }}
        >
          {dir.subtitle}
        </p>
      </div>
      <div
        className="rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-[0.16em]"
        style={{
          borderColor: `${colors.navy}18`,
          color: `${colors.navy}aa`,
          backgroundColor: "rgba(255,255,255,0.72)",
          fontFamily: fonts.body,
          fontWeight: 700,
        }}
      >
        {dir.format}
      </div>
    </div>
  );
}

/* ─── Demo data fallback so the lab is useful even with no live retreats ─── */
const DEMO: RetreatCard[] = [
  {
    id: "demo-1",
    slug: "fall-2026-shop-hop",
    title: "Stitched Together with Chelsea",
    subtitle: "Fall 2026 · Shop Hop Weekend",
    tagline: "come stitch with us",
    location: "Lehi, Utah",
    venue: "The Gathering Barn",
    startDate: "2026-10-20",
    endDate: "2026-10-23",
    capacity: 24,
    status: "open",
    heroMediaId: null,
  },
  {
    id: "demo-2",
    slug: "spring-2027-quilted-vest",
    title: "Quilted Vest Retreat",
    subtitle: "Spring 2027",
    tagline: "make something cozy",
    location: "St. George, Utah",
    venue: "Red Cliffs Lodge",
    startDate: "2027-12-04",
    endDate: "2027-12-07",
    capacity: 18,
    status: "sold-out",
    heroMediaId: null,
  },
  {
    id: "demo-3",
    slug: "spring-2026-retreat",
    title: "Spring 2026 Retreat",
    subtitle: "A weekend of sewing and snacks",
    tagline: "thanks for joining",
    location: "Utah",
    venue: null,
    startDate: "2026-04-29",
    endDate: "2026-05-02",
    capacity: null,
    status: "past",
    heroMediaId: null,
  },
];

export default function RetreatCardDesignLabPage({ retreats }: { retreats: RetreatCard[] }) {
  const theme = usePalette();
  const [activeId, setActiveId] = useState(DIRECTIONS[0].id);
  const [showAll, setShowAll] = useState(true);

  const sample = useMemo(() => (retreats.length ? retreats.slice(0, 6) : DEMO), [retreats]);
  const active = DIRECTIONS.find((d) => d.id === activeId) ?? DIRECTIONS[0];
  const visible = showAll ? DIRECTIONS : [active];

  return (
    <div className="min-h-dvh" style={{ backgroundColor: colors.cream }}>
      <section className="relative overflow-hidden px-6 pb-12 pt-28 text-center">
        <Blob className="left-1/4 top-10 h-80 w-80" color={theme.blob1} />
        <Blob className="bottom-0 right-1/4 h-64 w-64" color={theme.blob2} />
        <div className="relative z-10 mx-auto max-w-4xl">
          <div
            className="mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-widest"
            style={{ fontFamily: fonts.body, fontWeight: 700, backgroundColor: `${theme.accent}12`, color: theme.accent }}
          >
            <Sparkles size={14} />
            Retreat Card Lab
          </div>
          <h1 className="mb-4 text-5xl leading-tight sm:text-6xl" style={{ fontFamily: fonts.logo, color: colors.navy }}>
            Five Retreat
            <span style={{ fontFamily: fonts.heading, color: theme.warm, marginLeft: 10, fontSize: "1.12em" }}>
              Card Directions
            </span>
          </h1>
          <p
            className="mx-auto max-w-2xl leading-relaxed"
            style={{ fontFamily: fonts.body, fontWeight: 500, color: `${colors.navy}aa`, letterSpacing: "0.02em" }}
          >
            Five distinct product-card treatments for the retreats grid, each built from the
            brand palette, Playfair + Quicksand + Caveat type, and the stitching motif. Pick one
            — or mix.
          </p>
        </div>
      </section>

      {/* controls */}
      <section className="sticky top-[73px] z-30 px-6 pb-8 backdrop-blur-md">
        <div
          className="mx-auto max-w-6xl rounded-2xl border p-4"
          style={{ borderColor: `${colors.tan}22`, backgroundColor: `${colors.cream}f0` }}
        >
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {DIRECTIONS.map((d) => {
                const on = activeId === d.id && !showAll;
                return (
                  <button
                    key={d.id}
                    onClick={() => {
                      setActiveId(d.id);
                      setShowAll(false);
                    }}
                    className={`cursor-pointer rounded-full px-3 py-2 text-xs uppercase tracking-[0.08em] transition-all ${on ? "text-white" : "bg-white"}`}
                    style={{ fontFamily: fonts.body, fontWeight: 700, ...(on ? { backgroundColor: theme.accent } : { color: `${colors.navy}bb` }) }}
                  >
                    {d.name}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setShowAll(true)}
              className={`cursor-pointer rounded-full px-4 py-2 text-xs uppercase tracking-[0.08em] ${showAll ? "text-white" : ""}`}
              style={{ fontFamily: fonts.body, fontWeight: 700, ...(showAll ? { backgroundColor: colors.forestGreen } : { backgroundColor: "white", color: `${colors.navy}bb` }) }}
            >
              Show all 5
            </button>
          </div>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="mx-auto grid max-w-6xl gap-12">
          {visible.map((dir) => (
            <section key={dir.id} className="grid gap-5">
              <DirectionHeader dir={dir} index={DIRECTIONS.indexOf(dir)} />
              <motion.div
                key={dir.id}
                initial="hidden"
                animate="visible"
                variants={stagger}
                className={`grid grid-cols-1 gap-5 ${dir.cols}`}
              >
                {sample.map((r, i) => (
                  <motion.div key={r.id} variants={scaleIn} custom={i}>
                    {dir.render(r, i)}
                  </motion.div>
                ))}
              </motion.div>
            </section>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
